/**
 * Admin.
 *
 * Five things live here, and they decide what the agent is allowed to do:
 * which brands exist and what their refund policy is, what mode each
 * brand × intent runs in, which models play which role, who has access, and
 * the app-wide knobs that belong to no single brand.
 *
 * The screen never decides authorization. It reads `canAdminister` /
 * `canPromote` off each row and disables what the caller may not do — a
 * disabled control is a courtesy, the API refusing is the actual rule. Nor does
 * it validate a refund policy: the server owns that (`validateRefundPolicyOverride`
 * in @sophie/determinism) and returns every problem at once, which this screen
 * lists. A second copy of those rules in a form validator is a second copy that
 * can disagree with the first.
 */

import { Fragment, useState } from 'react';
import {
  AlertTriangle,
  Check,
  ChevronDown,
  ChevronRight,
  KeyRound,
  Plus,
  Trash2,
} from 'lucide-react';
import {
  useAppConfig,
  useBrands,
  useCreateBrand,
  useInvite,
  useModels,
  usePeople,
  usePromote,
  useRemoveUser,
  useRestoreUser,
  useSetRole,
  useRollout,
  useUpdateAppConfig,
  useWipeDashboard,
  useUpdateBrand,
  useUpdateModel,
  type BrandRow,
  type ModelRow,
  type PersonRow,
  type RolloutMode,
  type RolloutRow,
  type FollowUpSettings,
} from '../api/admin';
import { ApiError } from '../api/client';
import { isSuperadmin, useSession } from '../auth/session';
import { Badge, Button, Card, CardHeader, Table, Td, Th } from '../components/ui/primitives';
import { cn } from '../lib/cn';
import { AlertsTab } from './AdminAlerts';

const TABS = ['Brands', 'Rollout', 'Models', 'People', 'Config', 'Alerts'] as const;
type Tab = (typeof TABS)[number];

const MODE_TONE: Record<RolloutMode, 'neutral' | 'fact'> = {
  shadow: 'neutral',
  auto: 'fact',
};

const ROLES = ['superadmin', 'brand_admin', 'ops_agent', 'auditor', 'viewer'] as const;

export function AdminPage() {
  const [tab, setTab] = useState<Tab>('Brands');

  return (
    <div className="mx-auto max-w-[1400px] px-5 py-5">
      <div role="tablist" aria-label="Admin sections" className="mb-4 flex flex-wrap gap-2">
        {TABS.map((option) => (
          <button
            key={option}
            type="button"
            role="tab"
            aria-selected={tab === option}
            onClick={() => setTab(option)}
            className={cn(
              'rounded-sm px-2.5 py-1 text-xs font-medium transition-colors duration-fast',
              tab === option
                ? 'bg-ink-900 text-paper'
                : 'bg-surface text-ink-500 shadow-hairline hover:text-ink-900',
            )}
          >
            {option}
          </button>
        ))}
      </div>

      {tab === 'Brands' ? <Brands /> : null}
      {tab === 'Rollout' ? <Rollout /> : null}
      {tab === 'Models' ? <Models /> : null}
      {tab === 'People' ? <People /> : null}
      {tab === 'Config' ? <AppConfig /> : null}
      {tab === 'Alerts' ? <AlertsTab /> : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Shared
// ---------------------------------------------------------------------------

function Loading() {
  return <p className="px-4 py-6 text-sm text-ink-400">Loading…</p>;
}

/** Lists every problem the server reported, not just the first. */
function Problem({ error }: { error: unknown }) {
  if (!error) return null;
  const problems = error instanceof ApiError && error.problems.length > 0 ? error.problems : null;
  const message = error instanceof ApiError ? error.message : 'Something went wrong.';

  return (
    <div className="flex items-start gap-2 border-b border-line bg-stop-100 px-4 py-2.5 text-xs text-stop-700">
      <AlertTriangle size={14} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
      {problems ? (
        <ul className="space-y-0.5">
          {problems.map((problem) => (
            <li key={problem}>{problem}</li>
          ))}
        </ul>
      ) : (
        <p>{message}</p>
      )}
    </div>
  );
}

function Field({
  label,
  hint,
  value,
  onChange,
  disabled,
}: {
  label: string;
  hint?: string;
  value: string;
  onChange: (next: string) => void;
  disabled?: boolean;
}) {
  return (
    <label className="block">
      <span className="eyebrow">{label}</span>
      <input
        type="text"
        inputMode="numeric"
        value={value}
        disabled={disabled}
        onChange={(event) => onChange(event.target.value)}
        placeholder="inherit"
        className="mt-1 w-full rounded-sm bg-surface px-2 py-1 text-sm text-ink-900 shadow-hairline placeholder:text-ink-300 disabled:opacity-50"
      />
      {hint ? <span className="mt-0.5 block text-2xs text-ink-400">{hint}</span> : null}
    </label>
  );
}

// ---------------------------------------------------------------------------
// Brands
// ---------------------------------------------------------------------------

function Brands() {
  const { data, isPending, error } = useBrands();
  const session = useSession();
  const superadmin = session.data ? isSuperadmin(session.data) : false;
  const [openId, setOpenId] = useState<string | null>(null);
  const [creating, setCreating] = useState(false);

  if (isPending) return <Card><Loading /></Card>;
  if (error) return <Card><Problem error={error} /></Card>;

  return (
    <Card>
      <CardHeader
        title="Brands"
        meta={`${data.length} configured`}
        action={
          <span className="flex items-center gap-3">
            <span className="text-xs text-ink-400">
              Keys are write-only. The environment wins over anything stored here.
            </span>
            {superadmin && !creating ? (
              <Button size="sm" onClick={() => setCreating(true)}>
                <Plus size={13} strokeWidth={2} aria-hidden /> New brand
              </Button>
            ) : null}
          </span>
        }
      />
      {creating ? <NewBrandForm onDone={() => setCreating(false)} /> : null}
      <Table>
        <thead>
          <tr>
            <Th className="w-8" />
            <Th>Brand</Th>
            <Th>Slug</Th>
            <Th>Mode</Th>
            <Th>UltraCart key</Th>
            <Th>Refund policy</Th>
          </tr>
        </thead>
        <tbody>
          {data.map((brand) => {
            const open = openId === brand.id;
            return (
              // The key belongs on the Fragment: a brand renders two sibling
              // rows and React keys the outermost element of each item.
              <Fragment key={brand.id}>
                <tr>
                  <Td>
                    <button
                      type="button"
                      aria-expanded={open}
                      aria-label={`Settings for ${brand.name}`}
                      onClick={() => setOpenId(open ? null : brand.id)}
                      className="text-ink-400 hover:text-ink-900"
                    >
                      {open ? (
                        <ChevronDown size={15} strokeWidth={2} aria-hidden />
                      ) : (
                        <ChevronRight size={15} strokeWidth={2} aria-hidden />
                      )}
                    </button>
                  </Td>
                  <Td className="font-medium text-ink-900">
                    {brand.name}
                    {brand.active ? null : (
                      <Badge tone="stop" className="ml-2">
                        inactive
                      </Badge>
                    )}
                  </Td>
                  <Td className="font-mono text-xs text-ink-500">{brand.slug}</Td>
                  <Td>
                    <Badge tone={MODE_TONE[brand.mode]}>{brand.mode}</Badge>
                  </Td>
                  <Td>
                    <CredentialState brand={brand} />
                  </Td>
                  <Td className="text-xs text-ink-500">{describePolicy(brand.refundPolicy)}</Td>
                </tr>
                {open ? (
                  <tr>
                    <Td colSpan={6} className="bg-surface-sunken p-0">
                      <BrandSettings brand={brand} />
                    </Td>
                  </tr>
                ) : null}
              </Fragment>
            );
          })}
        </tbody>
      </Table>
    </Card>
  );
}

/** Blank means "inherit the standard policy" — not zero. */
const asText = (value: unknown): string =>
  value === undefined || value === null ? '' : String(value);

/** The ladder as typed: "50, 70, 80". Blank means inherit. */
function parseLadder(text: string): number[] | null {
  const trimmed = text.trim();
  if (trimmed === '') return null;
  return trimmed
    .split(/[,\s]+/)
    .filter(Boolean)
    .map((part) => Number(part));
}

function BrandSettings({ brand }: { brand: BrandRow }) {
  const update = useUpdateBrand();
  const policy = brand.refundPolicy ?? {};
  const storedLadder = Array.isArray(policy.saveLadder) ? (policy.saveLadder as number[]) : null;

  const [ladder, setLadder] = useState(storedLadder ? storedLadder.join(', ') : '');
  const [fields, setFields] = useState<Record<string, string>>({
    humanApprovalAboveCents: asText(policy.humanApprovalAboveCents),
    maxRefundsPerConversation: asText(policy.maxRefundsPerConversation),
    supplementGuaranteeDays: asText(policy.supplementGuaranteeDays),
    vitalityClubGuaranteeDays: asText(policy.vitalityClubGuaranteeDays),
  });

  const set = (key: string) => (next: string) => setFields((prev) => ({ ...prev, [key]: next }));
  const disabled = !brand.canAdminister || update.isPending;
  const preview = parseLadder(ladder);

  const save = () => {
    const refundPolicy: Record<string, unknown> = {};
    for (const [key, raw] of Object.entries(fields)) {
      const text = raw.trim();
      if (text === '') continue; // omitted means inherit
      // Sent as typed. A non-numeric value is rejected by the server with a
      // message naming the field, which is better than silently coercing it.
      refundPolicy[key] = Number(text);
    }
    const parsed = parseLadder(ladder);
    if (parsed) refundPolicy.saveLadder = parsed;
    update.mutate({ id: brand.id, refundPolicy });
  };

  return (
    <div>
      <Problem error={update.error} />
      <div className="px-4 py-3">
        <div className="mb-3 max-w-xl">
          <label className="block">
            <span className="eyebrow">Save ladder — escalating offers, in order</span>
            <input
              type="text"
              value={ladder}
              disabled={disabled}
              onChange={(event) => setLadder(event.target.value)}
              placeholder="inherit (30, 40, 50)"
              className="mt-1 w-full rounded-sm bg-surface px-2 py-1 font-mono text-sm text-ink-900 shadow-hairline placeholder:text-ink-300 disabled:opacity-50"
            />
          </label>
          <p className="mt-1 text-2xs text-ink-400">
            Up to three rungs, ascending. The customer hears the first; the second exists only
            because they declined it and still want to cancel. A clear refusal ends the ladder
            immediately, whatever rung it reached.
          </p>
          {preview && preview.length > 0 ? (
            <p className="mt-1.5 text-xs text-ink-500">
              {preview.map((percent, index) => (
                <span key={index}>
                  {index > 0 ? ' → ' : 'Offers: '}
                  <strong className="text-ink-900">{percent}%</strong>
                </span>
              ))}
              <span className="text-ink-400">
                {' '}
                · a chargeback threat goes straight to {Math.max(...preview)}%
              </span>
            </p>
          ) : null}
        </div>

        <div className="grid grid-cols-2 gap-3 md:grid-cols-4">
          <Field
            label="Approval above (cents)"
            hint="0 = every refund needs a person"
            value={fields.humanApprovalAboveCents ?? ''}
            onChange={set('humanApprovalAboveCents')}
            disabled={disabled}
          />
          <Field
            label="Max refunds / conversation"
            value={fields.maxRefundsPerConversation ?? ''}
            onChange={set('maxRefundsPerConversation')}
            disabled={disabled}
          />
          <Field
            label="Supplement guarantee (days)"
            value={fields.supplementGuaranteeDays ?? ''}
            onChange={set('supplementGuaranteeDays')}
            disabled={disabled}
          />
          <Field
            label="Vitality Club guarantee (days)"
            hint="never 365"
            value={fields.vitalityClubGuaranteeDays ?? ''}
            onChange={set('vitalityClubGuaranteeDays')}
            disabled={disabled}
          />
        </div>

        <div className="mt-3 flex items-center gap-3">
          <Button variant="primary" size="sm" onClick={save} disabled={disabled}>
            {update.isPending ? 'Saving…' : 'Save policy'}
          </Button>
          <Button
            size="sm"
            onClick={() => update.mutate({ id: brand.id, active: !brand.active })}
            disabled={disabled}
          >
            {brand.active ? 'Deactivate brand' : 'Activate brand'}
          </Button>
          {update.isSuccess && !update.isPending ? (
            <span className="flex items-center gap-1 text-xs text-ok-700">
              <Check size={13} strokeWidth={2.5} aria-hidden /> saved
            </span>
          ) : null}
          <p className="ml-auto text-2xs text-ink-400">
            Blank inherits the standard policy. Order value no longer changes the refund
            percentage — it only selects the workflow.
          </p>
        </div>

        <Connections brand={brand} disabled={disabled} />
      </div>
    </div>
  );
}

/**
 * The two identifiers that decide which external account a ticket touches.
 *
 * Kept apart from the policy form because they are a different KIND of change:
 * getting a refund band wrong costs margin, getting these wrong reads another
 * brand's customers.
 */
function Connections({ brand, disabled }: { brand: BrandRow; disabled: boolean }) {
  const update = useUpdateBrand();
  const [key, setKey] = useState('');
  const [zendeskId, setZendeskId] = useState(brand.zendeskBrandId ?? '');
  const [storeUrl, setStoreUrl] = useState(brand.storeUrl ?? '');
  const [orderIdPrefix, setOrderIdPrefix] = useState(brand.orderIdPrefix ?? '');
  const [supportEmail, setSupportEmail] = useState(brand.supportEmail ?? '');
  const [phoneUs, setPhoneUs] = useState(brand.supportPhoneUs ?? '');
  const [phoneIntl, setPhoneIntl] = useState(brand.supportPhoneIntl ?? '');
  const [productApiCode, setProductApiCode] = useState(brand.productApiCode ?? '');
  const [iterableKey, setIterableKey] = useState('');
  const [hubCode, setHubCode] = useState(brand.suppressionHubCode ?? '');

  const fromEnv = brand.commerceCredential.source === 'env';
  const iterableFromEnv = brand.marketingCredential.source === 'env';

  return (
    <div className="mt-4 border-t border-line pt-3">
      <p className="eyebrow mb-2 text-ink-500">Connections</p>

      <div className="grid gap-3 md:grid-cols-2">
        <div>
          <label htmlFor={`uc-${brand.id}`} className="eyebrow mb-1 block text-ink-500">
            UltraCart key
          </label>
          <input
            id={`uc-${brand.id}`}
            type="password"
            value={key}
            onChange={(event) => setKey(event.target.value)}
            placeholder={
              brand.commerceCredential.last4
                ? `set — ends ${brand.commerceCredential.last4}`
                : 'not set'
            }
            autoComplete="off"
            disabled={disabled || fromEnv}
            className="w-full rounded border border-line bg-surface px-2 py-1.5 font-mono text-sm text-ink-900 disabled:opacity-50"
          />
          <p className="mt-1 text-2xs text-ink-400">
            {fromEnv ? (
              <>
                Set in the environment as{' '}
                <code className="font-mono">{brand.commerceCredential.envName}</code>, which wins
                over anything typed here. Clear it there to manage the key from this screen.
              </>
            ) : (
              <>Stored write-only — never sent back to this page. Leave blank to keep it.</>
            )}
          </p>
        </div>

        <div>
          <label htmlFor={`zd-${brand.id}`} className="eyebrow mb-1 block text-ink-500">
            Zendesk brand ID
          </label>
          <input
            id={`zd-${brand.id}`}
            value={zendeskId}
            onChange={(event) => setZendeskId(event.target.value)}
            placeholder="e.g. 27935406587549"
            disabled={disabled}
            className="w-full rounded border border-line bg-surface px-2 py-1.5 font-mono text-sm text-ink-900"
          />
          <p className="mt-1 text-2xs text-ink-400">
            Zendesk's own numeric id, unrelated to the UltraCart prefix. Without it a ticket can
            only be routed by someone typing a slug.
          </p>
        </div>

        <div>
          <label htmlFor={`store-${brand.id}`} className="eyebrow mb-1 block text-ink-500">
            Store page URL
          </label>
          <input
            id={`store-${brand.id}`}
            value={storeUrl}
            onChange={(event) => setStoreUrl(event.target.value)}
            placeholder="e.g. https://purehealthresearch.com"
            disabled={disabled}
            className="w-full rounded border border-line bg-surface px-2 py-1.5 font-mono text-sm text-ink-900"
          />
          <p className="mt-1 text-2xs text-ink-400">
            The site customers order on. The agent uses it to point them at My Account for card
            updates and subscription management — unset, it names no site at all.
          </p>
        </div>

        <div>
          <label htmlFor={`prefix-${brand.id}`} className="eyebrow mb-1 block text-ink-500">
            Order-id prefix
          </label>
          <input
            id={`prefix-${brand.id}`}
            value={orderIdPrefix}
            onChange={(event) => setOrderIdPrefix(event.target.value)}
            placeholder='e.g. PHR1 for PHR1-3264577'
            disabled={disabled}
            className="w-full rounded border border-line bg-surface px-2 py-1.5 font-mono text-sm text-ink-900"
          />
          <p className="mt-1 text-2xs text-ink-400">
            The letters and digits before the dash in this brand's UltraCart order numbers. Sister
            brands use it to recognise this brand's orders when a customer emails the wrong inbox —
            unset, that redirect never names this brand.
          </p>
        </div>

        <TextInput
          id={`email-${brand.id}`}
          label="Support email"
          value={supportEmail}
          onChange={setSupportEmail}
          placeholder="e.g. support@purehealthresearch.com"
          disabled={disabled}
          hint="Named by workflows that send the customer to the brand's support address."
        />
        <TextInput
          id={`catalogue-${brand.id}`}
          label="Product catalogue code"
          value={productApiCode}
          onChange={setProductApiCode}
          placeholder="e.g. PHR"
          disabled={disabled}
          hint="This brand's code in the product API. Unset, pricing questions get the store page instead of a price."
        />
        <TextInput
          id={`phone-us-${brand.id}`}
          label="Support phone (US)"
          value={phoneUs}
          onChange={setPhoneUs}
          placeholder="e.g. (888) 863-6654"
          disabled={disabled}
          hint="Printed in Zoye's signature."
        />
        <TextInput
          id={`phone-intl-${brand.id}`}
          label="Support phone (outside US)"
          value={phoneIntl}
          onChange={setPhoneIntl}
          placeholder="e.g. +1 (571) 771-2774"
          disabled={disabled}
          hint="Printed in Zoye's signature."
        />

        <div>
          <label htmlFor={`iterable-${brand.id}`} className="eyebrow mb-1 block text-ink-500">
            Iterable API key
          </label>
          <input
            id={`iterable-${brand.id}`}
            type="password"
            value={iterableKey}
            onChange={(event) => setIterableKey(event.target.value)}
            placeholder={
              brand.marketingCredential.last4 ? `set — ends ${brand.marketingCredential.last4}` : 'not set'
            }
            autoComplete="off"
            disabled={disabled || iterableFromEnv}
            className="w-full rounded border border-line bg-surface px-2 py-1.5 font-mono text-sm text-ink-900 disabled:opacity-50"
          />
          <p className="mt-1 text-2xs text-ink-400">
            {iterableFromEnv ? (
              <>
                Set in the environment as{' '}
                <code className="font-mono">{brand.marketingCredential.envName}</code>, which wins over
                anything typed here.
              </>
            ) : (
              <>
                A server-side key from this brand's own Iterable project. Zoye uses it to remove
                customers from marketing emails in this project only. Stored write-only.
              </>
            )}
          </p>
        </div>
        <TextInput
          id={`hub-${brand.id}`}
          label="Suppression Hub code"
          value={hubCode}
          onChange={setHubCode}
          placeholder="e.g. enoa"
          disabled={disabled}
          hint="This brand's list in the Suppression Hub. Blank uses the hub brand whose name matches exactly."
        />
      </div>

      <div className="mt-3 flex items-center gap-3">
        <Button
          size="sm"
          disabled={disabled || update.isPending}
          onClick={() =>
            update.mutate(
              {
                id: brand.id,
                // Omitted when untouched. '' CLEARS the stored key, and a blank
                // box means "leave it alone", not "delete it".
                ...(key.trim() ? { ultracartKey: key.trim() } : {}),
                ...(iterableKey.trim() ? { iterableKey: iterableKey.trim() } : {}),
                ...(hubCode.trim() !== (brand.suppressionHubCode ?? '')
                  ? { suppressionHubCode: hubCode.trim() }
                  : {}),
                // Only when changed: the Zendesk mapping is superadmin-only,
                // and a brand admin saving their store URL must not be refused
                // over a field they never touched.
                ...(zendeskId.trim() !== (brand.zendeskBrandId ?? '')
                  ? { zendeskBrandId: zendeskId.trim() }
                  : {}),
                ...(storeUrl.trim() !== (brand.storeUrl ?? '')
                  ? { storeUrl: storeUrl.trim() }
                  : {}),
                ...(orderIdPrefix.trim() !== (brand.orderIdPrefix ?? '')
                  ? { orderIdPrefix: orderIdPrefix.trim() }
                  : {}),
                ...(supportEmail.trim() !== (brand.supportEmail ?? '')
                  ? { supportEmail: supportEmail.trim() }
                  : {}),
                ...(phoneUs.trim() !== (brand.supportPhoneUs ?? '')
                  ? { supportPhoneUs: phoneUs.trim() }
                  : {}),
                ...(phoneIntl.trim() !== (brand.supportPhoneIntl ?? '')
                  ? { supportPhoneIntl: phoneIntl.trim() }
                  : {}),
                ...(productApiCode.trim() !== (brand.productApiCode ?? '')
                  ? { productApiCode: productApiCode.trim() }
                  : {}),
              },
              {
                onSuccess: () => {
                  setKey('');
                  setIterableKey('');
                },
              },
            )
          }
        >
          {update.isPending ? 'Saving…' : 'Save connections'}
        </Button>

        {brand.commerceCredential.last4 && !fromEnv ? (
          <button
            type="button"
            className="text-xs text-ink-400 underline hover:text-stop-700"
            disabled={disabled || update.isPending}
            onClick={() => {
              if (window.confirm(`Remove the stored UltraCart key for ${brand.name}?`)) {
                update.mutate({ id: brand.id, ultracartKey: '' });
              }
            }}
          >
            remove key
          </button>
        ) : null}

        {brand.marketingCredential.last4 && !iterableFromEnv ? (
          <button
            type="button"
            className="text-xs text-ink-400 underline hover:text-stop-700"
            disabled={disabled || update.isPending}
            onClick={() => {
              if (window.confirm(`Remove the stored Iterable key for ${brand.name}?`)) {
                update.mutate({ id: brand.id, iterableKey: '' });
              }
            }}
          >
            remove Iterable key
          </button>
        ) : null}
      </div>
    </div>
  );
}

function TextInput({
  id,
  label,
  value,
  onChange,
  placeholder,
  hint,
  disabled,
  type = 'text',
}: {
  id: string;
  label: string;
  value: string;
  onChange: (next: string) => void;
  placeholder?: string;
  hint?: string;
  disabled?: boolean;
  type?: 'text' | 'password' | 'number';
}) {
  return (
    <div>
      <label htmlFor={id} className="eyebrow mb-1 block text-ink-500">
        {label}
      </label>
      <input
        id={id}
        type={type}
        value={value}
        onChange={(event) => onChange(event.target.value)}
        placeholder={placeholder}
        disabled={disabled}
        autoComplete="off"
        className="w-full rounded border border-line bg-surface px-2 py-1.5 font-mono text-sm text-ink-900 disabled:opacity-50"
      />
      {hint ? <p className="mt-1 text-2xs text-ink-400">{hint}</p> : null}
    </div>
  );
}

/**
 * Create a brand — superadmin only (2026-10-05). The server owns validation
 * and returns every problem at once; this form only collects. A new brand
 * starts in shadow: nothing reaches a customer until Rollout says so.
 */
function NewBrandForm({ onDone }: { onDone: () => void }) {
  const create = useCreateBrand();
  const [form, setForm] = useState({
    name: '',
    slug: '',
    zendeskBrandId: '',
    guaranteeDays: '',
    ultracartKey: '',
    storeUrl: '',
    orderIdPrefix: '',
    supportEmail: '',
    supportPhoneUs: '',
    supportPhoneIntl: '',
    productApiCode: '',
  });
  // The slug follows the name until someone types in it.
  const [slugEdited, setSlugEdited] = useState(false);
  const set = (key: keyof typeof form) => (value: string) =>
    setForm((current) => ({ ...current, [key]: value }));
  const slugFrom = (name: string) =>
    name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/^-+|-+$/g, '');
  const optional = (value: string) => (value.trim() === '' ? undefined : value.trim());

  return (
    <div className="border-b border-line bg-surface-sunken px-4 py-4">
      <p className="eyebrow mb-1 text-ink-500">New brand</p>
      <p className="mb-3 text-xs text-ink-400">
        Starts in <strong>shadow</strong>: Zoye drafts, but nothing reaches a customer until you
        promote it in Rollout. Grant people access in People.
      </p>
      <Problem error={create.error} />

      <div className="mt-2 grid gap-3 md:grid-cols-3">
        <TextInput
          id="new-name"
          label="Name *"
          value={form.name}
          onChange={(value) =>
            setForm((current) => ({
              ...current,
              name: value,
              ...(slugEdited ? {} : { slug: slugFrom(value) }),
            }))
          }
          placeholder="e.g. PureHealth Research"
        />
        <TextInput
          id="new-slug"
          label="Slug *"
          value={form.slug}
          onChange={(value) => {
            setSlugEdited(true);
            set('slug')(value);
          }}
          placeholder="e.g. purehealth-research"
          hint="Lowercase words joined by hyphens. Fixed once created: the UltraCart env key derives from it."
        />
        <TextInput
          id="new-zendesk"
          label="Zendesk brand ID *"
          value={form.zendeskBrandId}
          onChange={set('zendeskBrandId')}
          placeholder="e.g. 27935406587549"
          hint="Routes this brand's tickets. Numeric, from the Zendesk URL."
        />
        <TextInput
          id="new-guarantee"
          label="Money-back guarantee (days) *"
          type="number"
          value={form.guaranteeDays}
          onChange={set('guaranteeDays')}
          placeholder="e.g. 365 or 30"
          hint="Required, so no brand silently inherits the 365-day supplement window."
        />
        <TextInput
          id="new-key"
          label="UltraCart key"
          type="password"
          value={form.ultracartKey}
          onChange={set('ultracartKey')}
          placeholder="optional, can be added later"
          hint="Without it, this brand's tickets go to a person."
        />
        <TextInput
          id="new-store"
          label="Store page URL"
          value={form.storeUrl}
          onChange={set('storeUrl')}
          placeholder="e.g. https://purehealthresearch.com"
        />
        <TextInput
          id="new-prefix"
          label="Order-id prefix"
          value={form.orderIdPrefix}
          onChange={set('orderIdPrefix')}
          placeholder="e.g. PHR1"
        />
        <TextInput
          id="new-email"
          label="Support email"
          value={form.supportEmail}
          onChange={set('supportEmail')}
          placeholder="e.g. support@brand.com"
        />
        <TextInput
          id="new-catalogue"
          label="Product catalogue code"
          value={form.productApiCode}
          onChange={set('productApiCode')}
          placeholder="e.g. PHR"
        />
        <TextInput
          id="new-phone-us"
          label="Support phone (US)"
          value={form.supportPhoneUs}
          onChange={set('supportPhoneUs')}
          placeholder="e.g. (888) 863-6654"
        />
        <TextInput
          id="new-phone-intl"
          label="Support phone (outside US)"
          value={form.supportPhoneIntl}
          onChange={set('supportPhoneIntl')}
          placeholder="e.g. +1 (571) 771-2774"
        />
      </div>

      <div className="mt-3 flex items-center gap-3">
        <Button
          size="sm"
          disabled={create.isPending}
          onClick={() =>
            create.mutate(
              {
                name: form.name.trim(),
                slug: form.slug.trim(),
                zendeskBrandId: form.zendeskBrandId.trim(),
                guaranteeDays: Number(form.guaranteeDays),
                ultracartKey: optional(form.ultracartKey),
                storeUrl: optional(form.storeUrl),
                orderIdPrefix: optional(form.orderIdPrefix),
                supportEmail: optional(form.supportEmail),
                supportPhoneUs: optional(form.supportPhoneUs),
                supportPhoneIntl: optional(form.supportPhoneIntl),
                productApiCode: optional(form.productApiCode),
              },
              { onSuccess: onDone },
            )
          }
        >
          {create.isPending ? 'Creating…' : 'Create brand'}
        </Button>
        <button type="button" className="text-xs text-ink-400 hover:text-ink-900" onClick={onDone}>
          Cancel
        </button>
      </div>
    </div>
  );
}

function CredentialState({ brand }: { brand: BrandRow }) {
  return brand.commerceCredential.configured ? (
    <Badge tone="fact">
      <Check size={11} strokeWidth={2.5} aria-hidden /> set
    </Badge>
  ) : (
    <span className="flex items-center gap-1.5">
      <Badge tone="neutral">
        <KeyRound size={11} strokeWidth={2} aria-hidden /> missing
      </Badge>
      <code className="text-2xs text-ink-400">{brand.commerceCredential.envName}</code>
    </span>
  );
}

function describePolicy(config: Record<string, unknown>): string {
  const keys = Object.keys(config ?? {});
  if (keys.length === 0) return 'standard (inherited)';
  return `${keys.length} override${keys.length === 1 ? '' : 's'}`;
}

// ---------------------------------------------------------------------------
// Rollout
// ---------------------------------------------------------------------------

function Rollout() {
  const { data, isPending, error } = useRollout();
  const promote = usePromote();

  if (isPending) return <Card><Loading /></Card>;
  if (error) return <Card><Problem error={error} /></Card>;

  // The one irreversible-feeling act on this screen gets one plain-words
  // confirmation. Going back to shadow never asks — switching it off must
  // work at 3am without an argument.
  const flip = (row: RolloutRow, mode: RolloutMode) => {
    if (
      mode === 'auto' &&
      !window.confirm(
        `${row.brandName} replies to customers by itself, and its refunds and ` +
          'account changes land in Approvals as "Pre-approved" - still executed ' +
          'by a person, never on their own. Turn it on?',
      )
    ) {
      return;
    }
    promote.mutate({ brandId: row.brandId, mode });
  };

  return (
    <Card>
      <CardHeader title="Rollout" meta="One switch per brand. Everything starts in shadow." />

      <Problem error={promote.error} />

      <Table>
        <thead>
          <tr>
            <Th>Brand</Th>
            <Th>Mode</Th>
            <Th>Switch</Th>
          </tr>
        </thead>
        <tbody>
          {data.map((row) => (
            <tr key={row.brandId}>
              <Td className="font-medium text-ink-900">
                {row.brandName}
                {row.active ? null : (
                  <Badge tone="stop" className="ml-2">
                    inactive
                  </Badge>
                )}
              </Td>
              <Td>
                <Badge tone={MODE_TONE[row.mode]}>{row.mode}</Badge>
              </Td>
              <Td>
                <div className="flex gap-1">
                  {(['shadow', 'auto'] as RolloutMode[]).map((mode) => (
                    <button
                      key={mode}
                      type="button"
                      disabled={!row.canPromote || mode === row.mode || promote.isPending}
                      onClick={() => flip(row, mode)}
                      className={cn(
                        'rounded-sm px-2 py-0.5 text-2xs font-semibold transition-colors duration-fast',
                        mode === row.mode
                          ? 'bg-ink-900 text-paper'
                          : 'bg-surface text-ink-500 shadow-hairline hover:text-ink-900',
                        'disabled:pointer-events-none disabled:opacity-40',
                      )}
                    >
                      {mode}
                    </button>
                  ))}
                </div>
              </Td>
            </tr>
          ))}
        </tbody>
      </Table>

      <p className="border-t border-line px-4 py-2.5 text-xs text-ink-400">
        Shadow observes: drafts appear in the console and nothing reaches a customer. Auto is live:
        replies send themselves and escalations hand the Zendesk ticket to the CS group. Refunds and
        other money always wait for a person in Approvals, whatever the switch says.
      </p>
    </Card>
  );
}

// ---------------------------------------------------------------------------
// Models
// ---------------------------------------------------------------------------

function Models() {
  const { data, isPending, error } = useModels();
  const update = useUpdateModel();
  const [drafts, setDrafts] = useState<Record<string, string>>({});

  if (isPending) return <Card><Loading /></Card>;
  if (error) return <Card><Problem error={error} /></Card>;

  const judge = data.find((m) => m.role === 'judge' && m.active);
  const agent = data.find((m) => m.role === 'agent_loop' && m.active);
  const sameModel =
    judge && agent && `${judge.provider}:${judge.model}` === `${agent.provider}:${agent.model}`;

  return (
    <Card>
      <CardHeader title="Models" meta="Which model plays which role" />

      {sameModel ? (
        <div className="flex items-start gap-2 border-b border-line bg-stop-100 px-4 py-2.5 text-xs text-stop-700">
          <AlertTriangle size={14} strokeWidth={1.75} className="mt-px shrink-0" aria-hidden />
          <p>
            The judge and the agent are the same model. A model cannot mark its own homework — the
            eval harness will refuse to score until these differ.
          </p>
        </div>
      ) : null}

      <Problem error={update.error} />

      <Table>
        <thead>
          <tr>
            <Th>Role</Th>
            <Th>Provider</Th>
            <Th>Model</Th>
            <Th className="text-right">$ / Mtok in</Th>
            <Th className="text-right">$ / Mtok out</Th>
            <Th />
            <Th>Active</Th>
          </tr>
        </thead>
        <tbody>
          {data.map((model) => {
            const draft = drafts[model.id] ?? model.model;
            const dirty = draft.trim() !== model.model;
            return (
              <tr key={model.id}>
                <Td className="font-medium text-ink-900">{model.role.replace(/_/g, ' ')}</Td>
                <Td className="text-ink-500">
                  {model.provider}
                  {model.keyPresent === false ? (
                    // Without the key this role's pipeline cannot run at all —
                    // the single most important fact on this table.
                    <Badge tone="stop" className="ml-1.5">
                      no API key
                    </Badge>
                  ) : null}
                </Td>
                <Td>
                  <input
                    type="text"
                    value={draft}
                    aria-label={`Model id for ${model.role}`}
                    onChange={(event) =>
                      setDrafts((prev) => ({ ...prev, [model.id]: event.target.value }))
                    }
                    className="w-64 rounded-sm bg-surface px-2 py-1 font-mono text-xs text-ink-900 shadow-hairline"
                  />
                </Td>
                <PriceCell
                  model={model}
                  field="inputCostMicroPerMTok"
                  busy={update.isPending}
                  onSave={(value) => update.mutate({ id: model.id, inputCostMicroPerMTok: value })}
                />
                <PriceCell
                  model={model}
                  field="outputCostMicroPerMTok"
                  busy={update.isPending}
                  onSave={(value) => update.mutate({ id: model.id, outputCostMicroPerMTok: value })}
                />
                <Td>
                  <Button
                    variant="primary"
                    size="sm"
                    disabled={!dirty || update.isPending}
                    onClick={() => update.mutate({ id: model.id, model: draft.trim() })}
                  >
                    Save
                  </Button>
                </Td>
                <Td>
                  <button
                    type="button"
                    disabled={update.isPending}
                    onClick={() => update.mutate({ id: model.id, active: !model.active })}
                    className="disabled:pointer-events-none disabled:opacity-40"
                  >
                    <Badge tone={model.active ? 'fact' : 'neutral'}>
                      {model.active ? 'active' : 'off'}
                    </Badge>
                  </button>
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <p className="border-t border-line px-4 py-2.5 text-xs text-ink-400">
        Ids are pinned deliberately — an alias like <code className="text-2xs">gemini-flash-latest</code>{' '}
        would let the model change under a recorded eval score. To see what a provider accepts:{' '}
        <code className="text-2xs">dokku run aics pnpm --filter @sophie/llm run models</code>
      </p>
      <p className="border-t border-line px-4 py-2.5 text-xs text-ink-400">
        Prices are in dollars per million tokens, taken from the provider&rsquo;s pricing page. They
        live here rather than in the code because they change without a deploy. A model left blank
        is <span className="font-medium">unpriced</span>: its runs still record their token counts,
        and report no cost at all rather than a zero. Filling a price in re-prices nothing
        retrospectively — the tokens are kept so an old run can be costed by hand.
      </p>
    </Card>
  );
}

/**
 * One price cell.
 *
 * Entered in dollars per million tokens, because that is the unit every
 * provider publishes; stored in micro-dollars, because that is the unit a
 * per-ticket cost has to be counted in. Blank means unpriced, and clearing the
 * field is a real edit rather than a no-op.
 */
function PriceCell({
  model,
  field,
  busy,
  onSave,
}: {
  model: ModelRow;
  field: 'inputCostMicroPerMTok' | 'outputCostMicroPerMTok';
  busy: boolean;
  onSave: (value: number | null) => void;
}) {
  const stored = model[field];
  const asDollars = stored === null ? '' : String(stored / 1_000_000);
  const [draft, setDraft] = useState<string | null>(null);
  const value = draft ?? asDollars;

  const commit = () => {
    setDraft(null);
    if (value.trim() === '') {
      if (stored !== null) onSave(null);
      return;
    }
    const dollars = Number(value);
    if (!Number.isFinite(dollars) || dollars < 0) return;
    const micro = Math.round(dollars * 1_000_000);
    if (micro !== stored) onSave(micro);
  };

  return (
    <Td className="text-right">
      <input
        type="text"
        inputMode="decimal"
        value={value}
        disabled={busy}
        placeholder="—"
        aria-label={`${field === 'inputCostMicroPerMTok' ? 'Input' : 'Output'} price per million tokens for ${model.role}`}
        onChange={(event) => setDraft(event.target.value)}
        onBlur={commit}
        onKeyDown={(event) => {
          if (event.key === 'Enter') event.currentTarget.blur();
        }}
        className="tabular w-20 rounded-sm bg-surface px-2 py-1 text-right text-xs text-ink-900 shadow-hairline"
      />
    </Td>
  );
}

// ---------------------------------------------------------------------------
// People
// ---------------------------------------------------------------------------

function People() {
  const session = useSession();
  const people = usePeople();
  const brands = useBrands();
  const setRole = useSetRole();
  const remove = useRemoveUser();
  const restore = useRestoreUser();
  const [inviting, setInviting] = useState(false);

  if (people.isPending || brands.isPending) return <Card><Loading /></Card>;
  if (people.error) return <Card><Problem error={people.error} /></Card>;

  const superadmin = session.data ? isSuperadmin(session.data) : false;
  const brandCount = brands.data?.length ?? 0;

  const confirmRemove = (person: PersonRow) => {
    const who = person.name ? `${person.name} (${person.email})` : person.email;
    if (
      window.confirm(
        `Remove ${who}?\n\nAn account that has never done anything is deleted outright. One with recorded history is deactivated instead — sign-in blocked, all access revoked — so the audit trail keeps saying who did what.`,
      )
    ) {
      remove.mutate({ userId: person.id });
    }
  };

  const changeRole = (person: PersonRow, role: string | null) => {
    const who = person.name ?? person.email;
    const question =
      role === null
        ? `Remove all access for ${who}?`
        : `Make ${who} ${ROLE_LABELS[role] ?? role} on every brand?`;
    if (window.confirm(question)) setRole.mutate({ userId: person.id, role });
  };

  return (
    <Card>
      <CardHeader
        title="People"
        meta={`${people.data.length} with accounts · one role each, on all ${brandCount} brands`}
        action={
          <Button size="sm" onClick={() => setInviting(!inviting)} aria-expanded={inviting}>
            <Plus size={13} strokeWidth={2.5} aria-hidden /> Add user
          </Button>
        }
      />

      {inviting ? <InviteForm onDone={() => setInviting(false)} /> : null}

      <Problem error={setRole.error} />
      <Problem error={remove.error} />
      <Problem error={restore.error} />

      <Table>
        <thead>
          <tr>
            <Th>Person</Th>
            <Th>Role</Th>
            <Th>Last seen</Th>
            <Th className="w-10" />
          </tr>
        </thead>
        <tbody>
          {people.data.map((person) => {
            const role = topRoleOf(person);
            const partial = role !== null && person.memberships.length < brandCount;
            const mixed = new Set(person.memberships.map((m) => m.role)).size > 1;
            return (
              <tr key={person.id} className={person.active ? undefined : 'opacity-60'}>
                <Td>
                  <div className="font-medium text-ink-900">
                    {person.name ?? person.email}
                    {person.active ? null : (
                      <Badge tone="stop" className="ml-2">
                        deactivated
                      </Badge>
                    )}
                  </div>
                  <div className="text-xs text-ink-400">{person.email}</div>
                </Td>
                <Td>
                  {person.active ? (
                    <div className="flex flex-wrap items-center gap-2">
                      <select
                        value={role ?? ''}
                        disabled={setRole.isPending}
                        onChange={(event) =>
                          changeRole(person, event.target.value === '' ? null : event.target.value)
                        }
                        aria-label={`Role for ${person.email}`}
                        className="rounded-sm bg-surface px-2 py-1 text-xs text-ink-900 shadow-hairline disabled:opacity-40"
                      >
                        <option value="">No access</option>
                        {ROLES.map((option) => (
                          <option key={option} value={option}>
                            {ROLE_LABELS[option]}
                          </option>
                        ))}
                      </select>
                      {role === null ? null : partial || mixed ? (
                        <span className="text-2xs text-hold-700">
                          {partial ? `only on ${person.memberships.length} of ${brandCount} brands` : 'mixed roles'}
                          {' · '}
                          <button
                            type="button"
                            disabled={setRole.isPending}
                            onClick={() => changeRole(person, role)}
                            className="underline hover:text-ink-900 disabled:opacity-40"
                          >
                            make it {ROLE_LABELS[role] ?? role} on all
                          </button>
                        </span>
                      ) : (
                        <span className="text-2xs text-ink-400">all brands</span>
                      )}
                    </div>
                  ) : (
                    <span className="text-xs text-ink-400">
                      sign-in blocked · history kept
                      {superadmin ? (
                        <>
                          {' · '}
                          <button
                            type="button"
                            disabled={restore.isPending}
                            onClick={() => restore.mutate({ userId: person.id })}
                            className="underline hover:text-ink-900 disabled:opacity-40"
                          >
                            restore
                          </button>
                        </>
                      ) : null}
                    </span>
                  )}
                </Td>
                <Td className="text-xs text-ink-400">
                  {person.lastSeenAt ? new Date(person.lastSeenAt).toLocaleDateString() : '—'}
                </Td>
                <Td>
                  {superadmin && person.active && person.id !== session.data?.userId ? (
                    <button
                      type="button"
                      disabled={remove.isPending}
                      onClick={() => confirmRemove(person)}
                      aria-label={`Remove ${person.email}`}
                      title="Remove user"
                      className="text-ink-400 transition-colors duration-fast hover:text-stop-700 disabled:opacity-40"
                    >
                      <Trash2 size={14} strokeWidth={1.75} aria-hidden />
                    </button>
                  ) : null}
                </Td>
              </tr>
            );
          })}
        </tbody>
      </Table>

      <p className="border-t border-line px-4 py-2.5 text-xs text-ink-400">
        Everyone has one role, on every brand, and a brand added later is granted to everyone at their
        role. A self-created account (first Google sign-in) starts with no access — signing in with a
        company address is not itself permission to see customer data. &ldquo;Last seen: —&rdquo;
        marks the ones who have never signed in. The last superadmin cannot be demoted. Removing a user
        deletes an untouched account and deactivates one with history — the audit trail never loses its
        names.
      </p>
    </Card>
  );
}

const ROLE_LABELS: Record<string, string> = {
  superadmin: 'Superadmin',
  brand_admin: 'Brand admin',
  ops_agent: 'Ops agent',
  auditor: 'Auditor',
  viewer: 'Viewer',
};

/** The person's role across brands: their highest (ROLES is highest first), or null. */
function topRoleOf(person: PersonRow): string | null {
  for (const role of ROLES) {
    if (person.memberships.some((membership) => membership.role === role)) return role;
  }
  return null;
}

/**
 * Pre-create an account so its access exists before the first sign-in. The
 * OIDC callback links by email, so when the person eventually signs in with
 * Google this row becomes their account — no duplicate is minted. The role
 * applies to every brand.
 */
function InviteForm({ onDone }: { onDone: () => void }) {
  const invite = useInvite();
  const [email, setEmail] = useState('');
  const [name, setName] = useState('');
  const [role, setRole] = useState<string>('ops_agent');

  const submit = () =>
    invite.mutate({ email: email.trim(), name: name.trim() || undefined, role }, { onSuccess: onDone });

  return (
    <div className="border-b border-line bg-surface-sunken">
      <Problem error={invite.error} />
      <div className="px-4 py-3">
        <div className="grid gap-3 md:grid-cols-3">
          <label className="block">
            <span className="eyebrow">Email</span>
            <input
              type="email"
              value={email}
              onChange={(event) => setEmail(event.target.value)}
              placeholder="name@theimpactbrands.com"
              autoComplete="off"
              className="mt-1 w-full rounded-sm bg-surface px-2 py-1 text-sm text-ink-900 shadow-hairline placeholder:text-ink-300"
            />
          </label>
          <label className="block">
            <span className="eyebrow">Name</span>
            <input
              type="text"
              value={name}
              onChange={(event) => setName(event.target.value)}
              placeholder="optional"
              autoComplete="off"
              className="mt-1 w-full rounded-sm bg-surface px-2 py-1 text-sm text-ink-900 shadow-hairline placeholder:text-ink-300"
            />
          </label>
          <label className="block">
            <span className="eyebrow">Role (all brands)</span>
            <select
              value={role}
              onChange={(event) => setRole(event.target.value)}
              className="mt-1 w-full rounded-sm bg-surface px-2 py-1.5 text-sm text-ink-900 shadow-hairline"
            >
              {ROLES.map((option) => (
                <option key={option} value={option}>
                  {ROLE_LABELS[option]}
                </option>
              ))}
            </select>
          </label>
        </div>

        <div className="mt-3 flex items-center gap-3">
          <Button variant="primary" size="sm" disabled={!email.trim() || invite.isPending} onClick={submit}>
            {invite.isPending ? 'Adding…' : 'Add user'}
          </Button>
          <Button size="sm" onClick={onDone} disabled={invite.isPending}>
            Cancel
          </Button>
          <p className="ml-auto text-2xs text-ink-400">
            The exact address they will use for Google sign-in — a typo mints an account nobody can
            reach.
          </p>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Config — app-wide, not per brand
// ---------------------------------------------------------------------------

/**
 * The quiet-customer follow-up (Deivydas 2026-10-15, Octocom's auto-resolve
 * follow-up): its switch, the quiet hours, and how many nudges.
 */
function FollowUpConfig({ settings }: { settings: FollowUpSettings }) {
  const update = useUpdateAppConfig();
  const [enabled, setEnabled] = useState(settings.enabled);
  const [hours, setHours] = useState(String(settings.hours));
  const [max, setMax] = useState(settings.max);
  const parsedHours = Number(hours);
  const validHours = hours.trim() !== '' && Number.isInteger(parsedHours) && parsedHours >= 1 && parsedHours <= 168;
  const dirty =
    validHours && (enabled !== settings.enabled || parsedHours !== settings.hours || max !== settings.max);

  return (
    <div className="mt-6 max-w-md border-t border-line pt-4">
      <div className="eyebrow">Follow-up on quiet customers</div>
      <Problem error={update.error} />
      <label className="mt-2 flex items-center gap-2 text-sm text-ink-900">
        <input type="checkbox" checked={enabled} onChange={(event) => setEnabled(event.target.checked)} />
        Send a follow-up when the customer goes quiet
      </label>
      <div className="mt-3 flex flex-wrap items-end gap-4">
        <label className="block">
          <span className="eyebrow">After (hours)</span>
          <input
            type="text"
            inputMode="numeric"
            value={hours}
            onChange={(event) => setHours(event.target.value)}
            className="mt-1 w-24 rounded-sm bg-surface px-2 py-1 text-sm text-ink-900 shadow-hairline"
          />
        </label>
        <label className="block">
          <span className="eyebrow">Follow-ups</span>
          <select
            value={max}
            onChange={(event) => setMax(Number(event.target.value))}
            className="mt-1 block rounded-sm bg-surface px-2 py-1 text-sm text-ink-900 shadow-hairline"
          >
            {[1, 2, 3].map((n) => (
              <option key={n} value={n}>
                {n}
              </option>
            ))}
          </select>
        </label>
      </div>
      <p className="mt-1.5 text-xs text-ink-400">
        When Zoye&rsquo;s last reply is this many hours old and the customer has not written, Zoye
        checks whether it is still waiting on them (a choice, a confirmation, an order number). If it
        is, it sends one short nudge asking for it: no amounts, no new offers, nothing claimed as
        done. If nothing is left for the customer to do, nothing is sent. A customer reply resets
        the count.
      </p>
      <p className="mt-1 text-2xs text-ink-400">
        Only for brands in auto, tickets still with Zoye and still <em>pending</em> in Zendesk. Keep
        the Zendesk pending&rarr;solved automation longer than these hours, or it solves the ticket
        before the nudge.
      </p>
      <div className="mt-3 flex items-center gap-3">
        <Button
          variant="primary"
          size="sm"
          disabled={!dirty || update.isPending}
          onClick={() => update.mutate({ followUp: { enabled, hours: parsedHours, max } })}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
        {!validHours ? (
          <span className="text-xs text-stop-700">Whole hours, 1–168.</span>
        ) : update.isSuccess && !dirty ? (
          <span className="flex items-center gap-1 text-xs text-ok-700">
            <Check size={13} strokeWidth={2.5} aria-hidden /> saved
          </span>
        ) : null}
      </div>
    </div>
  );
}

/**
 * Colleagues vs testers (Deivydas 2026-10-20): mail from an
 * @theimpactbrands.com address goes to a person with no reply; the addresses
 * listed here are answered like customers, so staff can test Zoye.
 */
function InternalTestersConfig({ testers }: { testers: string[] }) {
  const update = useUpdateAppConfig();
  const [text, setText] = useState(testers.join('\n'));
  const list = text
    .split(/[\s,;]+/)
    .map((entry) => entry.trim().toLowerCase())
    .filter((entry) => entry !== '');
  const valid = list.every((entry) => /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(entry));
  const dirty = valid && list.join('\n') !== testers.join('\n');

  return (
    <div className="mt-6 max-w-md border-t border-line pt-4">
      <div className="eyebrow">Internal testers</div>
      <Problem error={update.error} />
      <textarea
        value={text}
        onChange={(event) => setText(event.target.value)}
        rows={3}
        aria-label="Internal testers"
        className="mt-2 w-full rounded-sm bg-surface px-2 py-1 font-mono text-xs text-ink-900 shadow-hairline"
      />
      <p className="mt-1.5 text-xs text-ink-400">
        Mail from an @theimpactbrands.com address is a colleague&rsquo;s instruction: Zoye sends no
        reply, changes nothing, and hands it to a person as &ldquo;Internal request from
        &hellip;&rdquo; with the topic and order numbers. The addresses listed here (one per line)
        are answered like any customer, so they can test Zoye.
      </p>
      <div className="mt-3 flex items-center gap-3">
        <Button
          variant="primary"
          size="sm"
          disabled={!dirty || update.isPending}
          onClick={() => update.mutate({ internalTesters: list })}
        >
          {update.isPending ? 'Saving…' : 'Save'}
        </Button>
        {!valid ? (
          <span className="text-xs text-stop-700">Email addresses only, one per line.</span>
        ) : update.isSuccess && !dirty ? (
          <span className="flex items-center gap-1 text-xs text-ok-700">
            <Check size={13} strokeWidth={2.5} aria-hidden /> saved
          </span>
        ) : null}
      </div>
    </div>
  );
}

function AppConfig() {
  const { data, isPending, error } = useAppConfig();
  const update = useUpdateAppConfig();
  const wipe = useWipeDashboard();
  const [draft, setDraft] = useState<string | null>(null);

  if (isPending) return <Card><Loading /></Card>;
  if (error) return <Card><Problem error={error} /></Card>;

  const value = draft ?? String(data.replyDelayMinutes);
  const parsed = Number(value);
  const valid = value.trim() !== '' && Number.isInteger(parsed) && parsed >= 0 && parsed <= 1440;
  const dirty = valid && parsed !== data.replyDelayMinutes;

  return (
    <Card>
      <CardHeader title="Config" meta="Applies to every brand" />
      <Problem error={update.error} />

      <div className="px-4 py-4">
        <div className="max-w-md">
          <label className="block">
            <span className="eyebrow">Reply delay (minutes)</span>
            <input
              type="text"
              inputMode="numeric"
              value={value}
              onChange={(event) => setDraft(event.target.value)}
              className="mt-1 w-32 rounded-sm bg-surface px-2 py-1 text-sm text-ink-900 shadow-hairline"
            />
          </label>
          <p className="mt-1.5 text-xs text-ink-400">
            How long Zoye holds a reply it would send <em>automatically</em> before it goes to the
            customer — 0 sends immediately, up to 1440 (a day). A reply a person clicks send on is
            never delayed. The window is also the ops team&rsquo;s chance to catch one before it
            leaves.
          </p>
          <p className="mt-1 text-2xs text-ink-400">
            A held reply is checked again when its wait ends: if the customer wrote again it is
            dropped (the newer reply answers both), and if the ticket was handed off it is not
            sent. A handoff acknowledgment is never held. Pressing Send in the ticket sends at
            once.
          </p>

          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="primary"
              size="sm"
              disabled={!dirty || update.isPending}
              onClick={() =>
                update.mutate({ replyDelayMinutes: parsed }, { onSuccess: () => setDraft(null) })
              }
            >
              {update.isPending ? 'Saving…' : 'Save'}
            </Button>
            {!valid ? (
              <span className="text-xs text-stop-700">Whole minutes, 0–1440.</span>
            ) : update.isSuccess && !dirty ? (
              <span className="flex items-center gap-1 text-xs text-ok-700">
                <Check size={13} strokeWidth={2.5} aria-hidden /> saved
              </span>
            ) : null}
          </div>
        </div>

        <FollowUpConfig settings={data.followUp} />

        <InternalTestersConfig testers={data.internalTesters ?? []} />

        {/* Superadmin by construction: the config read 403s for anyone else,
            so this section only ever renders for a superadmin. */}
        <div className="mt-6 max-w-md border-t border-line pt-4">
          <div className="eyebrow">Dashboard data</div>
          <p className="mt-1.5 text-xs text-ink-400">
            Hides everything currently on the dashboard — every chart starts counting again from
            the moment you press it. Nothing is deleted: tickets, runs and the audit trail stay in
            the database; they just stop being counted. For clearing out test data before the
            pilot&rsquo;s numbers start to matter.
          </p>
          <Problem error={wipe.error} />
          <div className="mt-3 flex items-center gap-3">
            <Button
              variant="danger"
              size="sm"
              disabled={wipe.isPending}
              onClick={() => {
                if (
                  window.confirm(
                    'Hide everything currently on the dashboard? Charts start over from this moment. The data stays in the database, but there is no undo button.',
                  )
                ) {
                  wipe.mutate({});
                }
              }}
            >
              {wipe.isPending ? 'Wiping…' : 'Wipe dashboard data'}
            </Button>
            {wipe.isSuccess ? (
              <span className="flex items-center gap-1 text-xs text-ok-700">
                <Check size={13} strokeWidth={2.5} aria-hidden /> wiped
              </span>
            ) : null}
          </div>

          {data.wipeouts.length > 0 ? (
            <div className="mt-3">
              <div className="text-2xs font-semibold text-ink-500">
                {data.wipeouts.length} lifetime wipeout(s)
              </div>
              <ul className="mt-1 space-y-0.5">
                {data.wipeouts.map((entry) => (
                  <li key={entry.at} className="text-2xs text-ink-400">
                    <span className="tabular">{new Date(entry.at).toLocaleString()}</span> ·{' '}
                    {entry.by}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="mt-3 text-2xs text-ink-400">Never wiped.</p>
          )}
        </div>
      </div>
    </Card>
  );
}
